/**
 * English: React context for Agent Station session + transitional Limbic JWT (localStorage).
 * Thai: Context session Agent Station + Limbic JWT ชั่วคราว (localStorage)
 */
import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { clearAllChatSessionStorage } from '../utils/chatSessionId';
import { isOrgScopedApp, redirectToPortalLogin } from '../config/publicPath';
import { ENDPOINTS } from '../config/api';
import {
  clearStoredControlCredentials,
  setStoredDeviceCommandPassword,
  setStoredLoginUsername,
} from '../utils/deviceCommandPassword';

/** English: Roles allowed to send control commands. Thai: Role ที่ส่งคำสั่งควบคุมได้ */
const CONTROL_ROLES = new Set([
  'admin',
  'operator',
  'superadmin',
  'manager',
  'supervisor',
  'god',
]);

const TOKEN_EXPIRY_KEY = 'admin_token_expiry';
const TOKEN_ORG_KEY = 'admin_org_name';
const SELECTED_ORG_KEY = 'admin_selected_org';

function decodeJwtRole(token: string): string {
  if (!token) return 'unknown';
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return 'operator';
    const padded = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(padded + '='.repeat((4 - (padded.length % 4)) % 4)));
    const role =
      payload.role ??
      payload.userRole ??
      payload.user_role ??
      payload.userType ??
      payload.user_type;
    if (typeof role === 'string' && role) return role.toLowerCase();
    if (Array.isArray(payload.roles) && payload.roles[0]) return String(payload.roles[0]).toLowerCase();
    return 'operator';
  } catch {
    return 'operator';
  }
}

function decodeJwtOrg(token: string): string {
  if (!token) return '';
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return '';
    const padded = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(padded + '='.repeat((4 - (padded.length % 4)) % 4)));
    return (
      payload.org_name ??
      payload.org ??
      payload.organization ??
      payload.orgName ??
      ''
    );
  } catch {
    return '';
  }
}

function decodeJwtUserSubject(token: string): string {
  if (!token) return '';
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return '';
    const padded = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(padded + '='.repeat((4 - (padded.length % 4)) % 4)));
    const raw =
      payload.sub ??
      payload.user_id ??
      payload.userId ??
      payload.uid ??
      payload.username ??
      '';
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
    if (typeof raw === 'number') return String(raw);
    return '';
  } catch {
    return '';
  }
}

interface AuthContextValue {
  token: string;
  isAuthenticated: boolean;
  userRole: string;
  canControl: boolean;
  /** Org used for station filtering / chat — selected org or bootstrap login org. */
  userOrgName: string;
  /** Explicit chat/admin org selection (may be empty until picked). */
  selectedOrgName: string;
  userSubject: string;
  asSessionOk: boolean;
  setToken: (
    t: string,
    expiresIn?: number,
    orgName?: string,
    deviceCommandPassword?: string,
    loginUsername?: string,
  ) => void;
  /** Agent Station login (username/password only). */
  login: (username: string, password: string) => Promise<{ ok: true } | { ok: false; error: string }>;
  /** Exchange Limbic JWT for an active Limbic org and set it as selected. */
  selectOrg: (orgName: string) => Promise<{ ok: true } | { ok: false; error: string; reauth?: boolean }>;
  clearSelectedOrg: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  token: '',
  isAuthenticated: false,
  userRole: 'operator',
  canControl: true,
  userOrgName: '',
  selectedOrgName: '',
  userSubject: '',
  asSessionOk: false,
  setToken: () => {},
  login: async () => ({ ok: false, error: 'not ready' }),
  selectOrg: async () => ({ ok: false, error: 'not ready' }),
  clearSelectedOrg: () => {},
  logout: () => {},
});

interface AuthProviderProps {
  children?: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [token, setTokenState] = useState<string>(() => localStorage.getItem('admin_token') || '');
  const [loginOrgName, setLoginOrgName] = useState<string>(() => localStorage.getItem(TOKEN_ORG_KEY) || '');
  const [selectedOrgName, setSelectedOrgName] = useState<string>(
    () => localStorage.getItem(SELECTED_ORG_KEY) || '',
  );
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const [asSessionOk, setAsSessionOk] = useState(false);

  useEffect(() => {
    const checkExpiry = () => {
      const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
      if (expiry) {
        const expiryTime = parseInt(expiry, 10);
        if (Date.now() > expiryTime) {
          setIsExpired(true);
          setTokenState('');
          setLoginOrgName('');
          localStorage.removeItem('admin_token');
          localStorage.removeItem(TOKEN_EXPIRY_KEY);
          localStorage.removeItem(TOKEN_ORG_KEY);
          clearStoredControlCredentials();
          clearAllChatSessionStorage();
        }
      }
    };

    checkExpiry();
    const interval = setInterval(checkExpiry, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(ENDPOINTS.asAuthSession, { credentials: 'include' });
        if (cancelled) return;
        if (!res.ok) {
          setAsSessionOk(false);
          return;
        }
        const data = (await res.json().catch(() => ({}))) as { authenticated?: boolean };
        setAsSessionOk(Boolean(data.authenticated));
      } catch {
        if (!cancelled) setAsSessionOk(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const setToken = useCallback(
    (
      t: string,
      expiresInSeconds?: number,
      orgName?: string,
      deviceCommandPassword?: string,
      loginUsername?: string,
    ) => {
      setTokenState(t);
      if (t) {
        localStorage.setItem('admin_token', t);
        const expiryMs = Date.now() + (expiresInSeconds ?? 86400) * 1000;
        localStorage.setItem(TOKEN_EXPIRY_KEY, expiryMs.toString());
        if (orgName?.trim()) {
          const normalizedOrg = orgName.trim();
          setLoginOrgName(normalizedOrg);
          localStorage.setItem(TOKEN_ORG_KEY, normalizedOrg);
        }
        if (deviceCommandPassword !== undefined) {
          setStoredDeviceCommandPassword(deviceCommandPassword);
        }
        if (loginUsername !== undefined) {
          setStoredLoginUsername(loginUsername);
        }
      } else {
        setLoginOrgName('');
        localStorage.removeItem('admin_token');
        localStorage.removeItem(TOKEN_EXPIRY_KEY);
        localStorage.removeItem(TOKEN_ORG_KEY);
        clearStoredControlCredentials();
      }
      setIsExpired(false);
    },
    [],
  );

  const login = useCallback(
    async (username: string, password: string): Promise<{ ok: true } | { ok: false; error: string }> => {
      try {
        const res = await fetch(ENDPOINTS.asAuthLogin, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ username: username.trim(), password }),
        });
        const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
        if (!res.ok) {
          const err =
            typeof data.error === 'string'
              ? data.error
              : typeof data.detail === 'string'
                ? data.detail
                : res.statusText || 'login failed';
          return { ok: false, error: err };
        }
        const accessToken = typeof data.access_token === 'string' ? data.access_token : '';
        if (!accessToken) return { ok: false, error: 'missing access_token' };
        const expiresIn =
          typeof data.expires_in === 'number' && Number.isFinite(data.expires_in)
            ? data.expires_in
            : 86400;
        const org = typeof data.org_name === 'string' ? data.org_name : '';
        setToken(accessToken, expiresIn, org || undefined, password, username.trim());
        setAsSessionOk(true);
        return { ok: true };
      } catch (err) {
        return { ok: false, error: err instanceof Error ? err.message : String(err) };
      }
    },
    [setToken],
  );

  const selectOrg = useCallback(
    async (
      orgName: string,
    ): Promise<{ ok: true } | { ok: false; error: string; reauth?: boolean }> => {
      const org = orgName.trim();
      if (!org) return { ok: false, error: 'org_name required' };
      try {
        const res = await fetch(ENDPOINTS.asAuthExchangeOrg, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ org_name: org }),
        });
        const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
        if (!res.ok) {
          const err =
            typeof data.error === 'string'
              ? data.error
              : typeof data.detail === 'string'
                ? data.detail
                : res.statusText || 'exchange failed';
          if (err === 'reauth_required' || res.status === 401) {
            return { ok: false, error: err, reauth: true };
          }
          return { ok: false, error: err };
        }
        const accessToken = typeof data.access_token === 'string' ? data.access_token : '';
        if (!accessToken) return { ok: false, error: 'missing access_token' };
        const expiresIn =
          typeof data.expires_in === 'number' && Number.isFinite(data.expires_in)
            ? data.expires_in
            : 86400;
        const resolved = typeof data.org_name === 'string' ? data.org_name : org;
        setToken(accessToken, expiresIn, resolved);
        setSelectedOrgName(resolved);
        localStorage.setItem(SELECTED_ORG_KEY, resolved);
        return { ok: true };
      } catch (err) {
        return { ok: false, error: err instanceof Error ? err.message : String(err) };
      }
    },
    [setToken],
  );

  const clearSelectedOrg = useCallback(() => {
    setSelectedOrgName('');
    localStorage.removeItem(SELECTED_ORG_KEY);
  }, []);

  const logout = useCallback(() => {
    void fetch(ENDPOINTS.asAuthLogout, { method: 'POST', credentials: 'include' }).catch(() => {});
    setTokenState('');
    setLoginOrgName('');
    setSelectedOrgName('');
    setAsSessionOk(false);
    localStorage.removeItem('admin_token');
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    localStorage.removeItem(TOKEN_ORG_KEY);
    localStorage.removeItem(SELECTED_ORG_KEY);
    clearStoredControlCredentials();
    clearAllChatSessionStorage();
    setIsExpired(false);
    if (isOrgScopedApp()) {
      redirectToPortalLogin(false);
    }
  }, []);

  const isAuthenticated = !!token && !isExpired;
  const userRole = decodeJwtRole(token);
  const canControl = CONTROL_ROLES.has(userRole);
  const userOrgName = selectedOrgName || loginOrgName || decodeJwtOrg(token);
  const userSubject = decodeJwtUserSubject(token);

  return (
    <AuthContext.Provider
      value={{
        token,
        isAuthenticated,
        userRole,
        canControl,
        userOrgName,
        selectedOrgName,
        userSubject,
        asSessionOk,
        setToken,
        login,
        selectOrg,
        clearSelectedOrg,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
